/**
 * Two My account cards from Activity — `docs/activity.md` §12.4 and §12.5,
 * Board 17 D and F.
 *
 * **Email**: connect your own Outlook, or don't. Connected, it says which
 * mailbox and since when, and *Send my emails from* **My Outlook · Rowted**;
 * a token Microsoft stopped accepting shows the amber band and **Reconnect**.
 * The whole card is absent when the company has turned Outlook off, and
 * *Connect Outlook* waits until Rowted's app registration is in place
 * (§19 A3). The token itself never reaches this page — the server says
 * which address, nothing more.
 *
 * **Activity, the way you left it**: a card per device the Activity page has
 * remembered, newest first — this one tinted — each with what it opens on in
 * one line and **Reset**, which makes that device open as a first visit.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { monthDay, type MeResponse } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { activityHeaders } from '../lib/activity-device.js'
import { Button, Segmented } from '../components/primitives.js'
import { SettingsCard } from '../components/settings-card.js'
import { Glyph, type GlyphName } from '../activity/glyphs.js'

interface MailConnectionView {
  allowed: boolean
  ready: boolean
  connection: {
    address: string
    status: 'connected' | 'needs_reconnect'
    sendFrom: 'outlook' | 'rowted'
    connectedAt: string
  } | null
}

export function EmailCard({ me }: { me: MeResponse }) {
  const [params] = useSearchParams()
  const [view, setView] = useState<MailConnectionView | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const at = useRef<HTMLDivElement>(null)
  const justConnected = params.get('outlook') === 'connected'

  const load = useCallback(async () => {
    try {
      setView(await request<MailConnectionView>('/account/mail', { headers: activityHeaders() }))
    } catch {
      setView(null)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  /* Back from Microsoft (`?tab=email`): the card is where the person is looking. */
  useEffect(() => {
    if (view && params.get('tab') === 'email') at.current?.scrollIntoView({ block: 'center' })
  }, [view, params])

  const act = async (run: () => Promise<void>) => {
    setBusy(true)
    setProblem(null)
    try {
      await run()
    } catch (cause) {
      setProblem((cause as { message?: string }).message ?? 'That did not work.')
    } finally {
      setBusy(false)
    }
  }
  const connect = () => act(async () => {
    const { url } = await request<{ url: string }>('/account/mail/connect',
      { method: 'POST', body: {}, headers: activityHeaders() })
    window.location.assign(url)
  })
  const disconnect = () => act(async () => {
    await request('/account/mail/disconnect', { method: 'POST', body: {}, headers: activityHeaders() })
    await load()
  })
  const sendFrom = (value: 'outlook' | 'rowted') => act(async () => {
    setView((was) => (was?.connection ? { ...was, connection: { ...was.connection, sendFrom: value } } : was))
    await request('/account/mail', { method: 'PATCH', body: { sendFrom: value }, headers: activityHeaders() })
  })

  if (!view || !view.allowed) return null
  const connection = view.connection
  if (!connection && !view.ready) return null

  return (
    <div ref={at}>
      <SettingsCard title="Email" sub={`My account · ${me.user.name}`}>
        <div className="flex flex-col px-[18px] pb-4 pt-3">
          {!connection && (
            <div className="flex items-center gap-3">
              <span className="text-[12.5px] leading-[1.45] text-text-muted">
                Send from your own mailbox; customers’ replies come back here too.
              </span>
              <span className="flex-1" />
              <Button variant="primary" className="h-[30px] shrink-0 text-[12px]" disabled={busy}
                onClick={() => void connect()}>
                Connect Outlook
              </Button>
            </div>
          )}

          {connection?.status === 'needs_reconnect' && (
            <div role="note" className="flex items-center gap-3 rounded-[10px] border border-amber-band-line bg-amber-band-bg p-3 text-[12.5px] leading-[1.45] text-amber-band-text">
              <span className="min-w-0 flex-1">
                Outlook needs reconnecting — emails are going from Rowted until then.
              </span>
              <Button className="h-[30px] shrink-0 text-[12px]" disabled={busy} onClick={() => void connect()}>
                Reconnect
              </Button>
            </div>
          )}

          {connection && (
            <div className={cn('flex items-center gap-3 rounded-[10px] border p-3',
              connection.status === 'connected' ? 'border-accent-line bg-green-band-bg' : 'mt-2.5 border-line bg-surface')}>
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-info-line bg-info-bg text-info">
                <Glyph name="envelope" size={17} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <b className="text-[13.5px]">{connection.status === 'connected' ? 'Outlook connected' : 'Outlook'}</b>
                <span className="text-[12px] text-text-muted">
                  {connection.address} · since {monthDay(connection.connectedAt.slice(0, 10))}
                </span>
              </div>
              <Button className="h-[30px] shrink-0 text-[12px]" disabled={busy} onClick={() => void disconnect()}>
                Disconnect
              </Button>
            </div>
          )}

          {connection && (
            <div className="mt-3 flex flex-col">
              <span className="mb-[5px] text-[11.5px] font-medium text-text-muted">Send my emails from</span>
              <span className="self-start">
                <Segmented
                  label="Send my emails from"
                  size="large"
                  value={connection.sendFrom}
                  onChange={(value) => void sendFrom(value)}
                  options={[{ value: 'outlook', label: 'My Outlook' }, { value: 'rowted', label: 'Rowted' }]}
                />
              </span>
            </div>
          )}

          {connection && (
            <p className="m-0 mt-2.5 text-[12px] leading-[1.5] text-text-muted">
              Emails to contacts go from your mailbox and appear in your Sent items. Replies from contacts are
              copied into your Activity Inbox, attached to their records. Nothing else from your mailbox is kept.
            </p>
          )}

          {justConnected && connection?.status === 'connected' && (
            <p className="m-0 mt-2 text-[12px] font-semibold text-accent-dark">Outlook is connected.</p>
          )}
          {problem && <p role="alert" className="m-0 mt-2 text-[12.5px] text-danger">{problem}</p>}
        </div>
      </SettingsCard>
    </div>
  )
}

interface DeviceRow {
  deviceKey: string
  kind: 'computer' | 'tablet' | 'phone'
  name: string
  browser: string
  thisDevice: boolean
  lastUsed: string
  line: { tab: string; rest: string }
}

const DEVICE_GLYPH: Record<DeviceRow['kind'], GlyphName> = {
  computer: 'monitor',
  tablet: 'tablet',
  phone: 'phone_device',
}

/** *This computer*, *This iPad* — the device asking; any other by the name it gave itself. */
const titleOf = (device: DeviceRow): string => (device.thisDevice
  ? (device.kind === 'computer' ? 'This computer' : `This ${device.name}`)
  : device.name)

export function ActivityDevicesCard({ me }: { me: MeResponse }) {
  const [devices, setDevices] = useState<DeviceRow[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const { items } = await request<{ items: DeviceRow[] }>('/account/activity/devices',
        { headers: activityHeaders() })
      setDevices(items)
    } catch {
      /* Activity is the office app's (§5.3): somebody without it has no card. */
      setDevices(null)
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const reset = async (device: DeviceRow) => {
    setProblem(null)
    try {
      await request('/activity/state/reset', { method: 'POST', body: { deviceKey: device.deviceKey },
        headers: activityHeaders() })
      await load()
    } catch (cause) {
      setProblem((cause as { message?: string }).message ?? 'That did not reset.')
    }
  }

  if (!devices) return null

  return (
    <SettingsCard title="Activity, the way you left it"
      sub={`My account · ${me.user.name} · each device keeps its own, saved as you go`}>
      <div className="flex flex-col px-[18px] pb-4 pt-0.5">
        {devices.length === 0 && (
          <p className="m-0 mt-2.5 text-[12.5px] text-text-faint">Nothing yet — open Activity and it’s kept as you go.</p>
        )}
        {devices.map((device) => (
          <div key={device.deviceKey} role="group" aria-label={titleOf(device)}
            className={cn('mt-2.5 rounded-xl border px-3.5 py-3',
              device.thisDevice ? 'border-accent-line bg-green-band-bg' : 'border-line bg-surface')}>
            <div className="flex items-center gap-2.5">
              <span className={cn('inline-flex', device.thisDevice ? 'text-accent' : 'text-text-muted')}>
                <Glyph name={DEVICE_GLYPH[device.kind]} size={17} />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <b className="text-[13.5px]">{titleOf(device)}</b>
                <span className="text-[11.5px] text-text-muted">{device.browser} · {device.lastUsed}</span>
              </div>
              <Button className="h-[30px] shrink-0 text-[12px]" onClick={() => void reset(device)}>
                <Glyph name="refresh" size={14} />Reset
              </Button>
            </div>
            <div className="mt-2 text-[12px] leading-[1.55] text-text-body">
              <b>{device.line.tab}</b> · {device.line.rest}
            </div>
          </div>
        ))}
        <p className="m-0 mt-2.5 text-[11.5px] leading-[1.45] text-text-faint">
          A new device starts from the last one of its kind. Saved groups are yours on every device.
        </p>
        {problem && <p role="alert" className="m-0 mt-2 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </SettingsCard>
  )
}
